import { Eye, Plus } from 'lucide-react';
import CreateSessionDialog from './CreateSessionDialog';
import { useState } from 'react';
import { useDeleteVotingSessionMutation, useGetVotingSessionsQuery } from '@/store/api/votingApi';
import { Pencil, Trash2 } from 'lucide-react';
import EditSessionDialog from './EditSessionDialog';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
interface Session {
  id: number;
  title: string;
  description: string;
  startTime: string;
  endTime: string;
  round: number;
  image: string | null;
  remainingTime: {
    hours: number;
    minutes: number;
    seconds: number;
  };
}
export default function VotingSessions() {
  const [openDialog, setOpenDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [sessionData, setSessionData] = useState<Session | null>(null);
  const [deleteVotingSession] = useDeleteVotingSessionMutation();
  const [page, setPage] = useState(1);
  const limit = 5;
  const { data, isLoading } = useGetVotingSessionsQuery({
    page,
    limit,
  });
  const navigate = useNavigate();
  // const sessionsData = data?.data || [];
  const sessionsData = data?.data || [];
  const totalPages = data?.meta?.totalPages || 1;
  const handleEdit = (session: Session) => {
    setSessionData(session);
    setOpenEditDialog(true);
  };

  const handleDelete = async (session: Session) => {
    try {
      await deleteVotingSession({
        sessionId: Number(session.id),
      }).unwrap();
      toast.dismiss();
      toast.success('Voting session deleted successfully');
    } catch (err) {
      console.error('Failed to update user status', err);
    }
  };

  return (
    <>
      <div className="p-4 sm:p-6 lg:p-8 text-white">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold">Voting Sessions</h1>

            <p className="text-gray-400 mt-1 text-sm sm:text-base">
              Create and manage voting sessions with multiple rounds.
            </p>
          </div>

          <button
            onClick={() => setOpenDialog(true)}
            className="flex items-center justify-center gap-2 bg-blue-500/20 hover:bg-blue-500/30 px-4 py-2 rounded-lg text-sm sm:text-base"
          >
            <Plus size={18} />
            Create New Session
          </button>
        </div>

        <div className="space-y-4">
          {sessionsData.map((session, index) => (
            <div
              key={index}
              className="relative bg-[#0b1220] border border-gray-800 rounded-xl p-4 sm:p-6 hover:border-gray-600 transition"
            >
              {/* Edit Delete Icons */}
              <div className="absolute top-3 right-3 flex gap-2">
                <button
                  onClick={() => navigate(`/voting-details/${session?.id}`)}
                  title="Details"
                  className="text-blue-400 hover:text-blue-600 cursor-pointer transition"
                >
                  <Eye size={18} />
                </button>
                <button
                  onClick={() => handleEdit(session)}
                  title="Edit"
                  className="text-blue-400 hover:text-blue-600 cursor-pointer transition"
                >
                  <Pencil size={18} />
                </button>

                <button
                  onClick={() => handleDelete(session)}
                  title="Delete"
                  className="text-red-400 hover:text-red-600 cursor-pointer transition"
                >
                  <Trash2 size={18} />
                </button>
              </div>

              <div className="flex flex-col lg:flex-row lg:justify-between gap-4">
                <div className="flex-1">
                  <h2 className="text-lg sm:text-xl font-semibold">
                    {session.title} ( {session.round} Rounds )
                  </h2>

                  <p className="text-gray-400 text-sm mt-1">{session.description}</p>

                  <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-gray-400 mt-4">
                    <span className="bg-blue-500/20 text-blue-400 px-3 py-1 rounded-md text-xs sm:text-sm">
                      Remaining {session.remainingTime.hours}h {session.remainingTime.minutes}m{' '}
                      {session.remainingTime.seconds}s
                    </span>

                    {session.status ? (
                      <span className="flex items-center gap-1 text-green-400 text-xs sm:text-sm">
                        ● Ongoing
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-green-400 text-xs sm:text-sm"></span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {sessionsData.length === 0 ? (
          <div className="border border-dashed border-gray-700 rounded-xl p-10 text-center text-gray-400">
            <p className="text-lg font-medium">No voting sessions found</p>
            <p className="text-sm mt-1">
              Click <span className="text-blue-400 font-medium">Create New Session</span> to add
              your first voting session.
            </p>
          </div>
        ) : (
          sessionsData.length > 0 && (
            <div className="flex justify-center items-center gap-3 mt-6">
              <button
                disabled={page === 1 || isLoading}
                onClick={() => setPage((prev) => prev - 1)}
                className="px-3 py-1 bg-gray-800 rounded disabled:opacity-40"
              >
                Prev
              </button>

              <span className="text-sm text-gray-400">
                Page {page} of {totalPages}
              </span>

              <button
                disabled={page === totalPages || isLoading}
                onClick={() => setPage((prev) => prev + 1)}
                className="px-3 py-1 bg-gray-800 rounded disabled:opacity-40"
              >
                Next
              </button>
            </div>
          )
        )}

        {/* <p className="text-center text-gray-500 mt-6 text-sm">
          Displaying {sessionsData?.length} active sessions
        </p> */}
      </div>

      {openDialog && <CreateSessionDialog open={openDialog} setOpen={setOpenDialog} />}
      {openEditDialog && (
        <EditSessionDialog
          open={openEditDialog}
          setOpen={setOpenEditDialog}
          session={sessionData}
        />
      )}
    </>
  );
}
